import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import Axios from 'axios';
import { Helmet } from 'react-helmet-async';

import { UserContext, SnackbarContext, EngInfoContext } from './userContext';
import { BackDropLoad } from './LoadingBackDrop';
import { Loading } from './Loading';

const key = 'dqweqwe123y97qhduiasd$@^!*#&bjasdja&^&@!#';

/* ─────────── Inline icons ─────────── */

const Icon = {
  Person: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Phone: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72a2 2 0 0 1 1.72 2z" />
    </svg>
  ),
  Lock: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
  Eye: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  EyeOff: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="m1 1 22 22" />
      <path d="M17.94 17.94A10.06 10.06 0 0 1 12 19c-7 0-10-7-10-7a18 18 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 7 10 7a18 18 0 0 1-2.16 3.19" />
      <path d="M14.12 14.12A3 3 0 0 1 9.88 9.88" />
    </svg>
  ),
  ArrowRight: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  ),
  Check: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M5 12l5 5L20 7" />
    </svg>
  ),
  Close: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  ),
  Spinner: ({ className }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" fill="none" strokeDasharray="40 60" strokeLinecap="round" />
    </svg>
  ),
  Verified: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M9 12l2 2 4-4" />
      <path d="M12 2 4 5v6c0 5 3.5 9.5 8 11 4.5-1.5 8-6 8-11V5l-8-3z" />
    </svg>
  ),
  Bolt: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />
    </svg>
  ),
  Globe: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20M12 2a15 15 0 0 1 0 20 15 15 0 0 1 0-20z" />
    </svg>
  ),
  Sparkle: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="m12 3 2 5 5 2-5 2-2 5-2-5-5-2 5-2z" />
    </svg>
  ),
};

/* ─────────── Field - amber theme ─────────── */

function Field({
  label, icon, type = 'text', value, onChange, error, errorMsg, helper, required,
  autoComplete, inputMode, maxLength, trailing, autoFocus, placeholder,
}) {
  const [focused, setFocused] = useState(false);
  const showError = !!error;

  return (
    <div className="w-full">
      <label className="mb-1.5 block text-[10.5px] font-bold uppercase tracking-[0.14em] text-fg-tertiary">
        {label}
        {required && <span className="ml-1 text-amber">*</span>}
      </label>
      <div
        className={`
          relative flex h-12 items-center gap-2.5 rounded-lg px-3.5
          bg-surface-overlay/70
          border
          transition-[border-color,background-color,box-shadow] duration-200 ease-out-soft
          ${showError
            ? 'border-red-400 shadow-[0_0_0_3px_rgba(248,113,113,0.16)]'
            : focused
              ? 'border-amber bg-surface-overlay shadow-[0_0_0_3px_rgba(251,191,36,0.22)]'
              : 'border-amber/22 hover:border-amber/55'}
        `}
      >
        {icon && (
          <span className={`shrink-0 transition-colors duration-200 ${showError ? 'text-red-400' : focused ? 'text-amber' : 'text-amber/70'}`}>
            {icon}
          </span>
        )}
        <input
          type={type}
          value={value}
          onChange={onChange}
          required={required}
          autoComplete={autoComplete}
          inputMode={inputMode}
          maxLength={maxLength}
          autoFocus={autoFocus}
          placeholder={placeholder}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className="min-w-0 flex-1 bg-transparent text-[14px] font-medium text-fg-primary placeholder:text-fg-muted outline-none border-0 p-0"
        />
        {trailing}
      </div>
      <div className={`mt-1 min-h-[16px] text-[11px] leading-tight ${showError ? 'text-red-400' : 'text-fg-tertiary'}`}>
        {showError && errorMsg ? errorMsg : (helper || ' ')}
      </div>
    </div>
  );
}

const EyeToggle = ({ show, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-label={show ? 'Hide password' : 'Show password'}
    className="shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-md text-fg-tertiary hover:bg-surface-overlay hover:text-amber transition-colors duration-200"
  >
    {show ? <Icon.EyeOff className="h-[17px] w-[17px]" /> : <Icon.Eye className="h-[17px] w-[17px]" />}
  </button>
);

/* ─────────── Custom checkbox ─────────── */

function Check({ checked, onToggle, error, children }) {
  let style;
  if (checked) {
    style = {
      backgroundColor: '#FBBF24',
      borderColor: '#FBBF24',
      boxShadow: '0 0 0 3px rgba(251,191,36,0.22)',
    };
  } else if (error) {
    style = { borderColor: '#F87171', backgroundColor: 'rgba(248,113,113,0.10)' };
  } else {
    style = { borderColor: '#FBBF24', backgroundColor: 'rgba(251,191,36,0.10)' };
  }
  return (
    <label className="flex items-start gap-3 cursor-pointer select-none group">
      <span
        style={style}
        className="
          relative mt-px inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center
          rounded-md border-2
          transition-[background-color,border-color,box-shadow] duration-150 ease-out-soft
        "
      >
        {checked && <Icon.Check className="h-[14px] w-[14px] text-ink" />}
      </span>
      <input type="checkbox" checked={checked} onChange={onToggle} className="sr-only" />
      <span className="text-[12.5px] leading-snug text-fg-secondary group-hover:text-fg-primary transition-colors duration-150">
        {children}
      </span>
    </label>
  );
}

/* ─────────── Modal ─────────── */

function Modal({ open, onClose, children, lock = false }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (!lock && e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, lock]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4">
      <div
        aria-hidden
        className="absolute inset-0 bg-black/72 backdrop-blur-md animate-[fadeIn_180ms_ease-out]"
        onClick={lock ? undefined : onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        className="
          relative w-full max-w-md overflow-hidden rounded-2xl
          bg-surface-paper border border-amber/30
          p-6 sm:p-7
          animate-[fadeIn_180ms_ease-out]
          shadow-[0_24px_64px_-12px_rgba(0,0,0,0.85),0_0_64px_-8px_rgba(251,191,36,0.22)]
        "
      >
        <span aria-hidden className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-amber/60 to-transparent" />
        {children}
      </div>
    </div>
  );
}

/* ─────────── 6-segment OTP input ─────────── */

function OtpInput({ value, onChange, error, length = 6, autoFocus }) {
  const inputsRef = useRef([]);
  const digits = Array.from({ length }, (_, i) => value[i] || '');

  useEffect(() => {
    if (autoFocus && inputsRef.current[0]) {
      setTimeout(() => inputsRef.current[0].focus(), 50);
    }
  }, [autoFocus]);

  const setAt = (i, v) => {
    const next = digits.slice();
    next[i] = v;
    onChange(next.join('').slice(0, length));
  };

  const handleChange = (i) => (e) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (!raw) { setAt(i, ''); return; }
    // If user pastes/enters multiple chars, distribute
    if (raw.length > 1) {
      const chunk = raw.slice(0, length - i);
      const next = digits.slice();
      for (let k = 0; k < chunk.length; k += 1) next[i + k] = chunk[k];
      onChange(next.join('').slice(0, length));
      const last = Math.min(i + chunk.length, length - 1);
      setTimeout(() => inputsRef.current[last] && inputsRef.current[last].focus(), 0);
      return;
    }
    setAt(i, raw[0]);
    if (i < length - 1) setTimeout(() => inputsRef.current[i + 1] && inputsRef.current[i + 1].focus(), 0);
  };

  const handleKeyDown = (i) => (e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      e.preventDefault();
      const next = digits.slice();
      next[i - 1] = '';
      onChange(next.join(''));
      setTimeout(() => inputsRef.current[i - 1] && inputsRef.current[i - 1].focus(), 0);
    } else if (e.key === 'ArrowLeft' && i > 0) {
      inputsRef.current[i - 1] && inputsRef.current[i - 1].focus();
    } else if (e.key === 'ArrowRight' && i < length - 1) {
      inputsRef.current[i + 1] && inputsRef.current[i + 1].focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, length);
    if (pasted) {
      e.preventDefault();
      onChange(pasted);
      const last = Math.min(pasted.length - 1, length - 1);
      setTimeout(() => inputsRef.current[last] && inputsRef.current[last].focus(), 0);
    }
  };

  return (
    <div className="flex items-center justify-between gap-1.5 sm:gap-2">
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => { inputsRef.current[i] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[i]}
          onChange={handleChange(i)}
          onKeyDown={handleKeyDown(i)}
          onPaste={handlePaste}
          aria-label={`OTP digit ${i + 1}`}
          className={`
            h-12 w-full max-w-[52px] rounded-lg
            text-center text-[18px] font-bold tabular-nums
            bg-surface-overlay/70
            border-2
            text-fg-primary
            outline-none
            transition-[border-color,background-color,box-shadow,transform] duration-150 ease-out-soft
            ${error
              ? 'border-red-400'
              : digits[i]
                ? 'border-amber bg-surface-overlay shadow-[0_0_0_3px_rgba(251,191,36,0.18)]'
                : 'border-amber/30 hover:border-amber/55 focus:border-amber focus:bg-surface-overlay focus:shadow-[0_0_0_3px_rgba(251,191,36,0.22)]'}
          `}
        />
      ))}
    </div>
  );
}

/* ─────────── Brand panel ─────────── */

function BrandPanel() {
  return (
    <aside className="relative hidden md:flex isolate overflow-hidden bg-amber-gradient">
      {/* noise */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.08] mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        }}
      />
      {/* dark grid mask */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.10]"
        style={{
          backgroundImage: 'linear-gradient(rgba(10,10,11,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(10,10,11,0.6) 1px, transparent 1px)',
          backgroundSize: '56px 56px',
          maskImage: 'radial-gradient(ellipse 60% 70% at 50% 50%, #000, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(ellipse 60% 70% at 50% 50%, #000, transparent 80%)',
        }}
      />
      {/* dark radial accent */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{ background: 'radial-gradient(700px 360px at 100% 100%, rgba(10,10,11,0.18), transparent 60%)' }}
      />
      {/* floating orbs */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-12 -left-16 h-72 w-72 rounded-full opacity-50 animate-pulse"
        style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.25), transparent 70%)' }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-16 -right-12 h-80 w-80 rounded-full opacity-40"
        style={{ background: 'radial-gradient(circle, rgba(10,10,11,0.18), transparent 70%)' }}
      />

      <div className="relative flex flex-col justify-center p-10 lg:p-14 w-full">
        {/* Headline */}
        <div>
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-ink/24 bg-ink/8 px-3 py-1.5">
            <span aria-hidden className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-ink opacity-50" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-ink" />
            </span>
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink">For professionals</span>
          </div>
          <h1 className="text-[34px] lg:text-[44px] font-black leading-[1.04] tracking-[-0.038em] text-ink">
            Build your network<br />in construction.
          </h1>
          <p className="mt-5 max-w-[480px] text-[15px] leading-[1.6] text-ink/80">
            Join thousands of contractors, suppliers, engineers and workforce
            already listed on ESAY - India's most trusted construction directory.
          </p>

          {/* What you'll get */}
          <ul className="mt-6 grid grid-cols-1 gap-2.5 max-w-[480px]">
            {[
              'Verified profile with direct customer leads',
              'Featured in Pan-India search results',
              'Manage products, subscriptions, bookmarks',
              'Free engineering reference library access',
            ].map((t) => (
              <li key={t} className="flex items-center gap-2.5 text-[13.5px] font-medium text-ink/85">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-ink text-amber">
                  <Icon.Check className="h-3 w-3" />
                </span>
                {t}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </aside>
  );
}

/* ─────────── Step indicator ─────────── */

function Steps({ current = 1, total = 2 }) {
  const labels = ['Account', 'Verify'];
  return (
    <div className="mb-5 flex items-center gap-2">
      {Array.from({ length: total }).map((_, i) => {
        const idx = i + 1;
        const isDone = idx < current;
        const isActive = idx === current;
        return (
          <React.Fragment key={i}>
            <div className="inline-flex items-center gap-2">
              <span
                className={`
                  relative inline-flex h-7 w-7 items-center justify-center rounded-full
                  text-[11px] font-bold
                  transition-[background-color,color,box-shadow] duration-200
                  ${isDone
                    ? 'bg-amber text-ink shadow-[0_0_0_2px_rgba(251,191,36,0.22)]'
                    : isActive
                      ? 'bg-amber text-ink shadow-[0_0_0_3px_rgba(251,191,36,0.28)]'
                      : 'bg-amber/10 text-amber border border-amber/30'}
                `}
              >
                {isDone ? <Icon.Check className="h-3.5 w-3.5" /> : idx}
                {isActive && (
                  <span aria-hidden className="absolute -inset-0.5 rounded-full border border-amber/60 animate-ping opacity-60" />
                )}
              </span>
              <span className={`text-[11px] font-bold uppercase tracking-[0.14em] ${isActive || isDone ? 'text-amber' : 'text-fg-tertiary'}`}>
                {labels[i]}
              </span>
            </div>
            {i < total - 1 && (
              <span aria-hidden className={`mx-1 h-px flex-1 transition-colors duration-300 ${idx < current ? 'bg-amber' : 'bg-amber/20'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ═══════════════════ Component ═══════════════════ */

export const NewSignup = () => {
  const history = useNavigate();
  const { setUser } = useContext(UserContext);
  const { setSnack } = useContext(SnackbarContext);
  const { setEngInfoList } = useContext(EngInfoContext);

  const [loading, setLoading] = useState(true);
  const [metadata, setMetadata] = useState(null);

  const [fname, setFname] = useState('');
  const [lname, setLname] = useState('');
  const [num, setNum] = useState('');
  const [pass, setPass] = useState('');
  const [repass, setRePass] = useState('');
  const [wait, setWait] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [terms, setTerms] = useState(false);
  const [error, setError] = useState(0);

  const [otp, setOtp] = useState('');
  const [otptoken, setOtptoken] = useState('');
  const [otpOpen, setOtpOpen] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  // Resend countdown
  useEffect(() => {
    if (!otpOpen) return undefined;
    if (resendCountdown <= 0) return undefined;
    const t = setTimeout(() => setResendCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [otpOpen, resendCountdown]);

  useEffect(() => {
    window.scrollTo(0, 0);
    Axios.post('/new_apis/getsinglemetadata', { type: 'signup' }, { headers: { key } })
      .then((res) => {
        if (res.data.status) setMetadata(res.data.metadata[0]);
        else setMetadata({
          seo_title: 'Signup | ESAY',
          seo_desc: 'Create an account on ESAY - the construction industry directory.',
          seo_keywords: 'signup, register, esay',
        });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const getEngInfodata = async (tkn, uid) => {
    try {
      const res = await Axios.post('/new_apis/get_bookmarks', { user_id: uid }, { headers: { tkn } });
      if (res.data.status === 1) setEngInfoList(res.data.data.map((d) => d.id));
    } catch { /* noop */ }
  };

  const requestOtp = (silent = false) => {
    Axios.post('/new_apis/generateotp_signup_new', { mob: num, name: fname }, { headers: { key } }).then((res) => {
      if (res.data.status === 1) {
        setWait(false);
        setOtptoken(res.data.otptoken);
        setOtpOpen(true);
        setResendCountdown(30);
        if (!silent) setSnack({ message: res.data.msg, type: 'success', open: true });
      } else if (res.data.status === 2) {
        setWait(false);
        setSnack({ message: res.data.msg, type: 'error', open: true });
      }
    }).catch(() => {
      setWait(false);
      setSnack({ message: 'Network error. Please try again.', type: 'error', open: true });
    });
  };

  const signupNow = (e) => {
    e.preventDefault();
    setError(0);
    if (fname === '') {
      setError(1);
      setSnack({ message: 'Please enter your first name.', type: 'error', open: true });
      return;
    }
    if (num.length !== 10) {
      setError(2);
      setSnack({ message: 'Enter a valid 10-digit mobile number.', type: 'error', open: true });
      return;
    }
    if (pass.length < 5) {
      setError(3);
      setSnack({ message: 'Password must be at least 5 characters.', type: 'error', open: true });
      return;
    }
    if (pass !== repass) {
      setError(4);
      setSnack({ message: 'Passwords do not match.', type: 'error', open: true });
      return;
    }
    if (!terms) {
      setError(5);
      setSnack({ message: 'Please agree to the Terms and Conditions.', type: 'error', open: true });
      return;
    }
    setWait(true);
    setOtp('');
    requestOtp();
  };

  const submitOtp = (e) => {
    e.preventDefault();
    setError(0);
    if (otp.length < 4) {
      setError(11);
      setSnack({ message: 'Please enter the OTP.', type: 'error', open: true });
      return;
    }
    setWait(true);
    Axios.post(
      '/new_apis/validateotp_signup_new',
      { fname, lname, mob: num, pass, otp, otptoken },
      { headers: { key } },
    ).then((res) => {
      if (res.data.status === 1) {
        setWait(false);
        setOtpOpen(false);
        setUser(res.data.user_data);
        localStorage.setItem('estkn', res.data.token);
        getEngInfodata(res.data.token, res.data.user_data.user_id);
        setSnack({ message: res.data.msg, type: 'success', open: true, direction: 'left' });
        history('/myaccount/complete_profile');
      } else if (res.data.status === 2) {
        setWait(false);
        setSnack({ message: res.data.msg, type: 'error', open: true });
      }
    });
  };

  const resendOtp = () => {
    if (resendCountdown > 0) return;
    setOtp('');
    requestOtp(true);
    setSnack({ message: 'A new OTP has been sent to your mobile.', type: 'success', open: true });
  };

  if (loading) return <Loading />;

  return (
    <>
      {metadata && (
        <Helmet>
          <title>{metadata.seo_title}</title>
          <meta name="description" content={metadata.seo_desc} />
          <meta name="keywords" content={metadata.seo_keywords} />
        </Helmet>
      )}

      <BackDropLoad wait={wait} />

      {/* ═════ Split-screen layout ═════ */}
      <div className="grid grid-cols-1 md:grid-cols-2 min-h-[calc(100vh-56px)]">

        <BrandPanel />

        {/* RIGHT - Form panel */}
        <main className="relative flex items-center justify-center px-5 py-10 sm:px-8 md:px-10 lg:px-14 bg-surface-base overflow-hidden">
          {/* subtle radial */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(700px 320px at 50% -10%, rgba(251,191,36,0.10), transparent 55%), radial-gradient(500px 280px at 100% 110%, rgba(251,191,36,0.06), transparent 60%)',
            }}
          />

          <div className="relative w-full max-w-[460px]">
            {/* Mobile-only brand strip + login CTA */}
            <div className="md:hidden mb-7 flex items-center justify-between">
              <RouterLink to="/" className="inline-flex items-center gap-2.5">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-amber shadow-amber-glow">
                  <img src="/assets/ESAY.png" alt="" className="h-6 w-6 object-contain" />
                </span>
                <span className="text-[16px] font-extrabold tracking-[-0.02em] text-fg-primary leading-none">ESAY</span>
              </RouterLink>
              <RouterLink
                to="/login"
                className="inline-flex items-center gap-1 rounded-full border border-amber/40 bg-amber/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-amber hover:bg-amber/20"
              >
                Sign in
              </RouterLink>
            </div>

            {/* Step indicator */}
            <Steps current={otpOpen ? 2 : 1} />

            {/* Header */}
            <div className="mb-6">
              <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-amber/30 bg-amber/10 px-3 py-1.5">
                <span aria-hidden className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber opacity-60" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-amber" style={{ boxShadow: '0 0 10px rgba(251,191,36,0.7)' }} />
                </span>
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber">Get started</span>
              </div>
              <h2 className="text-[26px] sm:text-[30px] font-bold leading-[1.12] tracking-[-0.025em] text-fg-primary">
                Create your account<span className="text-amber">.</span>
              </h2>
              <div aria-hidden className="mt-3 h-[2px] w-12 rounded-full bg-gradient-to-r from-amber to-amber/0" />
              <p className="mt-3 text-[13.5px] text-fg-secondary leading-relaxed">
                It takes about a minute. We'll verify your mobile next.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={signupNow} noValidate className="flex flex-col gap-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <Field
                  label="First name"
                  required
                  icon={<Icon.Person className="h-[18px] w-[18px]" />}
                  value={fname}
                  onChange={(e) => setFname(e.target.value)}
                  error={error === 1}
                  errorMsg="Please enter your first name."
                  helper=" "
                  autoComplete="given-name"
                  placeholder="Jane"
                  autoFocus
                />
                <Field
                  label="Last name"
                  icon={<Icon.Person className="h-[18px] w-[18px]" />}
                  value={lname}
                  onChange={(e) => setLname(e.target.value)}
                  helper="Optional"
                  autoComplete="family-name"
                  placeholder="Doe"
                />
              </div>

              <Field
                label="Mobile number"
                required
                type="tel"
                icon={<Icon.Phone className="h-[18px] w-[18px]" />}
                value={num}
                onChange={(e) => setNum(e.target.value.replace(/\D/g, '').slice(0, 10))}
                error={error === 2}
                errorMsg="Enter a valid 10-digit mobile number."
                helper="We'll send a one-time code to verify."
                inputMode="numeric"
                maxLength={10}
                autoComplete="tel"
                placeholder="9876543210"
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <Field
                  label="Password"
                  required
                  type={showPass ? 'text' : 'password'}
                  icon={<Icon.Lock className="h-[18px] w-[18px]" />}
                  value={pass}
                  onChange={(e) => setPass(e.target.value)}
                  error={error === 3}
                  errorMsg="At least 5 characters."
                  helper="At least 5 characters."
                  autoComplete="new-password"
                  trailing={<EyeToggle show={showPass} onToggle={() => setShowPass((v) => !v)} />}
                />
                <Field
                  label="Confirm password"
                  required
                  type={showPass ? 'text' : 'password'}
                  icon={<Icon.Lock className="h-[18px] w-[18px]" />}
                  value={repass}
                  onChange={(e) => setRePass(e.target.value)}
                  error={error === 4}
                  errorMsg="Passwords do not match."
                  helper=" "
                  autoComplete="new-password"
                />
              </div>

              {/* Terms */}
              <div className="mt-1">
                <Check checked={terms} onToggle={() => setTerms((t) => !t)} error={error === 5}>
                  I agree to the{' '}
                  <RouterLink to="/terms" className="font-bold text-amber hover:text-amber-light underline-offset-2 hover:underline">
                    Terms and Conditions
                  </RouterLink>
                  .
                </Check>
                {error === 5 && (
                  <p className="mt-1.5 ml-[34px] text-[11px] text-red-400">Please agree to the Terms and Conditions.</p>
                )}
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={wait}
                className={`
                  group mt-5 inline-flex h-12 w-full items-center justify-center gap-2
                  rounded-lg
                  text-[12.5px] font-bold uppercase tracking-[0.12em]
                  transition-all duration-200 ease-out-soft
                  ${wait
                    ? 'bg-amber/30 text-ink/50 cursor-wait'
                    : 'bg-amber text-ink hover:bg-amber-light hover:shadow-[0_0_0_3px_rgba(251,191,36,0.32),0_10px_28px_-8px_rgba(251,191,36,0.55)] active:scale-[0.98]'}
                `}
              >
                {wait ? (
                  <>
                    <Icon.Spinner className="h-4 w-4 animate-spin" />
                    Sending OTP…
                  </>
                ) : (
                  <>
                    Continue
                    <Icon.ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>

            {/* Already a member */}
            <div className="
              mt-7 pt-5 border-t border-amber/15 text-center
              flex flex-col sm:flex-row sm:items-center sm:justify-center gap-2 sm:gap-3
            ">
              <span className="text-[13px] text-fg-secondary">Already a member?</span>
              <RouterLink
                to="/login"
                className="
                  inline-flex h-10 items-center justify-center gap-1 rounded-lg px-4
                  text-[12px] font-bold uppercase tracking-[0.1em]
                  border border-amber/40 text-amber
                  transition-[background-color,border-color] duration-200
                  hover:bg-amber/10 hover:border-amber/70
                "
              >
                Sign in instead
                <Icon.ArrowRight className="h-3.5 w-3.5" />
              </RouterLink>
            </div>
          </div>
        </main>
      </div>

      {/* ═════ OTP Modal - 6-segment input + resend timer ═════ */}
      <Modal open={otpOpen} onClose={() => setOtpOpen(false)}>
        <button
          type="button"
          onClick={() => setOtpOpen(false)}
          aria-label="Close"
          className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-full text-fg-tertiary hover:bg-surface-overlay hover:text-amber transition-colors duration-200"
        >
          <Icon.Close className="h-4 w-4" />
        </button>

        <div className="mb-1 inline-flex items-center gap-1.5 rounded-full border border-amber/30 bg-amber/10 px-2.5 py-1">
          <Icon.Sparkle className="h-3 w-3 text-amber" />
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber">Step 2 of 2</span>
        </div>
        <h3 className="mt-3 text-[20px] font-bold tracking-[-0.018em] text-fg-primary leading-tight">
          Verify your mobile
        </h3>
        <p className="mt-1.5 text-[13px] text-fg-secondary leading-relaxed">
          We sent a 6-digit code to{' '}
          <span className="font-semibold text-amber">+91 {num}</span>.
        </p>

        <form onSubmit={submitOtp} noValidate className="mt-5">
          <div className="mb-1.5 flex items-center justify-between">
            <label className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-fg-tertiary">
              Enter OTP <span className="ml-1 text-amber">*</span>
            </label>
            <button
              type="button"
              onClick={resendOtp}
              disabled={resendCountdown > 0}
              className={`
                text-[11px] font-bold uppercase tracking-[0.1em]
                transition-colors duration-200
                ${resendCountdown > 0
                  ? 'text-fg-tertiary cursor-not-allowed'
                  : 'text-amber hover:text-amber-light'}
              `}
            >
              {resendCountdown > 0 ? `Resend in ${resendCountdown}s` : 'Resend OTP'}
            </button>
          </div>

          <OtpInput value={otp} onChange={setOtp} error={error === 11} length={6} autoFocus />

          {error === 11 && (
            <div className="mt-2 text-[11px] text-red-400">Please enter the OTP.</div>
          )}

          <div className="mt-5 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
            <button
              type="button"
              onClick={() => setOtpOpen(false)}
              className="
                inline-flex h-10 items-center justify-center rounded-lg px-4
                text-[12px] font-bold uppercase tracking-[0.08em]
                border border-border-subtle text-fg-secondary
                transition-[background-color,border-color,color] duration-200
                hover:bg-surface-overlay hover:text-fg-primary hover:border-amber/30
              "
            >
              Back
            </button>
            <button
              type="submit"
              disabled={wait || otp.length < 4}
              className={`
                group inline-flex h-10 items-center justify-center gap-1.5 rounded-lg px-5
                text-[12px] font-bold uppercase tracking-[0.1em]
                transition-all duration-200 ease-out-soft
                ${wait || otp.length < 4
                  ? 'bg-amber/30 text-ink/50 cursor-wait'
                  : 'bg-amber text-ink hover:bg-amber-light hover:shadow-[0_0_0_3px_rgba(251,191,36,0.28),0_8px_22px_-8px_rgba(251,191,36,0.5)] active:scale-[0.98]'}
              `}
            >
              {wait ? <Icon.Spinner className="h-3.5 w-3.5 animate-spin" /> : null}
              Verify &amp; create
              {!wait && <Icon.ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />}
            </button>
          </div>
        </form>

        {/* Footer note */}
        <div className="mt-4 pt-4 border-t border-border-subtle text-center text-[11px] text-fg-tertiary">
          Didn't receive the code? Check your SMS inbox or wait{' '}
          {resendCountdown > 0 ? `${resendCountdown} seconds` : 'and tap Resend'}.
        </div>
      </Modal>
    </>
  );
};
